import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, filter, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {BuildingService} from '@shared/services/building.service';
import {
    loadBuilding,
    loadBuildingFailure,
    loadBuildingSuccess,
    loadLocation,
    loadLocationFailure,
    loadLocationSuccess,
    loadMeter,
    loadMeterFailure,
    loadMeterSuccess,
    loadUser,
    loadUserFailure,
    loadUserSuccess,
} from 'src/app/state/actions';
import {of} from 'rxjs';
import {UserService} from '@shared/services/user.service';
import {MeterService} from 'src/app/meter/meter.service';
import {LocationService} from '@shared/services/location.service';
import {Store} from '@ngrx/store';
import {selectUser} from 'src/app/state/selectors';

@Injectable()
export class Effects {
    private actions$ = inject(Actions);
    private buildingService = inject(BuildingService);
    private meterService = inject(MeterService);
    private userService = inject(UserService);
    private locationService = inject(LocationService);
    private readonly store = inject(Store);

    loadBuilding$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadBuilding),
            switchMap(({buildingNo}) =>
                this.buildingService.getBuilding(buildingNo).pipe(
                    map(buildingDetails => loadBuildingSuccess({building: buildingDetails})),
                    catchError(() => of(loadBuildingFailure({error: 'An error occurred'}))),
                ),
            ),
        ),
    );

    loadMeter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadMeter),
            switchMap(({buildingNo, meterId}) =>
                this.meterService.getMeterData(buildingNo, meterId).pipe(
                    map(meterDetails => loadMeterSuccess({meter: meterDetails})),
                    catchError(() => of(loadMeterFailure({error: 'An error occurred'}))),
                ),
            ),
        ),
    );

    loadUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadUser),
            // Reducers run first, so 'success' means already loaded; exhaustMap drops
            // dispatches while a request is in flight.
            withLatestFrom(this.store.select(selectUser)),
            filter(([, userState]) => userState.status !== 'success'),
            exhaustMap(() =>
                this.userService.getUser().pipe(
                    map(user => loadUserSuccess({user: user})),
                    catchError(error => {
                        console.error(error);
                        return of(loadUserFailure({error: 'An error occurred'}));
                    }),
                ),
            ),
        ),
    );

    loadLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadLocation),
            switchMap(({buildingNo, locationId}) =>
                this.locationService.getLocation(buildingNo, locationId).pipe(
                    map(location => loadLocationSuccess({location: location})),
                    catchError(() => of(loadLocationFailure({error: 'An error occurred'}))),
                ),
            ),
        ),
    );
}
